import { Controller } from "@hotwired/stimulus"

// Hover loupe for admin battle preview. Desktop only (hover + fine pointer).
// When the source image reports natural size, the lens renders at 1:1 pixels;
// otherwise it falls back to the configured zoom factor.
//
// If an ancestor carries the admin-loupe-sync controller, this tile defers
// positioning to "admin-loupe:sync" CustomEvents so all tiles in the group
// mirror each other.
export default class extends Controller<HTMLElement> {
  static targets = ["lens"]
  static values = {
    src: String,
    naturalWidth: { type: Number, default: 0 },
    naturalHeight: { type: Number, default: 0 },
    zoom: { type: Number, default: 2.5 }
  }

  declare readonly lensTarget: HTMLElement
  declare readonly hasLensTarget: boolean
  declare readonly srcValue: string
  declare readonly naturalWidthValue: number
  declare readonly naturalHeightValue: number
  declare readonly zoomValue: number

  private standalone = false

  connect(): void {
    if (!this.hasLensTarget) return
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return
    if (!this.srcValue) return

    this.lensTarget.style.backgroundImage = `url("${this.srcValue}")`
    this.element.addEventListener("admin-loupe:sync", this.onSync as EventListener)

    this.standalone = !this.element.closest("[data-controller~='admin-loupe-sync']")
    if (this.standalone) {
      this.element.addEventListener("pointerenter", this.enter)
      this.element.addEventListener("pointermove", this.move)
      this.element.addEventListener("pointerleave", this.hide)
    }
  }

  disconnect(): void {
    this.element.removeEventListener("admin-loupe:sync", this.onSync as EventListener)
    if (this.standalone) {
      this.element.removeEventListener("pointerenter", this.enter)
      this.element.removeEventListener("pointermove", this.move)
      this.element.removeEventListener("pointerleave", this.hide)
    }
  }

  private enter = (event: PointerEvent): void => {
    this.move(event)
    this.lensTarget.classList.add("is-visible")
  }

  private hide = (): void => {
    this.lensTarget.classList.remove("is-visible")
  }

  private move = (event: PointerEvent): void => {
    const rect = this.element.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return
    const xpct = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1)
    const ypct = Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1)
    this.positionLens(xpct, ypct)
  }

  private onSync = (event: CustomEvent<{ xpct?: number; ypct?: number; active: boolean }>): void => {
    if (!event.detail.active) {
      this.hide()
      return
    }
    if (event.detail.xpct === undefined || event.detail.ypct === undefined) return
    this.positionLens(event.detail.xpct, event.detail.ypct)
    this.lensTarget.classList.add("is-visible")
  }

  private positionLens(xpct: number, ypct: number): void {
    const rect = this.element.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return

    const lensW = this.lensTarget.offsetWidth
    const lensH = this.lensTarget.offsetHeight
    const bgW = this.naturalWidthValue > 0 ? this.naturalWidthValue : rect.width * this.zoomValue
    const bgH = this.naturalHeightValue > 0 ? this.naturalHeightValue : rect.height * this.zoomValue

    const bgX = -xpct * bgW + lensW / 2
    const bgY = -ypct * bgH + lensH / 2

    this.lensTarget.style.backgroundSize = `${bgW}px ${bgH}px`
    this.lensTarget.style.backgroundPosition = `${bgX}px ${bgY}px`
    this.lensTarget.style.left = `${xpct * rect.width - lensW / 2}px`
    this.lensTarget.style.top = `${ypct * rect.height - lensH / 2}px`
  }
}
